import type { ActionTree } from 'vuex'
import dayjs from 'dayjs'
import type { Audience, AudienceOptInStats, AudienceState } from './types'
import { pruneSourceAudience } from './utils'
import type { RootState } from '@/store/modules/types'
import {
  convertCurrencyFilterToMinorUnits,
  fanFilterWithExtraConditions,
  mergeSearchStringToFanFilters,
} from '@/utils/filter/'
import { magicDownload } from '@/utils/html-element/'
import type {
  MessageList,
  MessageListChannel,
} from '@/api/message-lists/types'
import { convertToSanitizedCamelCase } from '@/utils/helpers'
import { getContext } from '@/utils/general'
import { requestApi } from '@/utils/requestApi'
import { correlateListSubscriptionChannel } from '@/utils/filter/listSubscriptionChannel'

// Carries the paired count request's promise on a rows failure, so a caller
// that specifically needs it for controller lifecycle tracking (reloadFans)
// isn't left with no way to reach a count request that may still be
// genuinely running once this action's normal `{ countPromise }` return
// value was never reached.
export interface AudienceFetchError extends Error {
  countPromise?: Promise<unknown>
  code?: string
}

const prepareAudienceFilter = (filter: unknown, searchString = '') => {
  const correlatedFilter = correlateListSubscriptionChannel(filter)
  return mergeSearchStringToFanFilters(
    convertCurrencyFilterToMinorUnits(correlatedFilter),
    searchString,
  )
}

export const audienceActions: ActionTree<AudienceState, RootState> = {
  async FETCH_MORE_AUDIENCE(
    { state, commit, dispatch },
    {
      searchString = '',
      top = 50,
      orderBy = { key: 'sysMtime', order: 'desc' },
      filter = { conditions: [], logic: [] },
      selectKeys = null,
      reload = false,
      skipCount = false,
      more = false,
      skip = undefined,
      // Deliberately separate signals: rows and count are independent
      // life cycles. A reload cancels both (fresh signal for each); a
      // load-more only replaces rows and must never be able to touch an
      // in-flight count, since load-more never dispatches a new one to
      // replace it with.
      signal = undefined,
      countSignal = undefined,
    },
  ) {
    let orderByStr
    if (Array.isArray(orderBy)) {
      orderByStr = orderBy
        .map(item => `${item.key} ${item.order}`)
        .join(', ')
        .concat(`, oid ${orderBy[0].order}`)
    }
    else {
      orderByStr = `${orderBy.key} ${orderBy.order}, oid ${orderBy.order}`
    }
    console.log('[FETCH_AUDIENCE] Original filter:', JSON.stringify(filter))
    console.log('[FETCH_AUDIENCE] searchString:', searchString)
    const mergedFilter = prepareAudienceFilter(filter, searchString)
    console.log('[FETCH_AUDIENCE] After merge:', JSON.stringify(mergedFilter))
    const filterStr = JSON.stringify(mergedFilter)
    console.log('[FETCH_AUDIENCE] Filter string:', filterStr)

    // Kept parallel with the rows fetch below (count is much slower to compute), but
    // its promise is still returned to the caller — not fire-and-forget — so the
    // caller can track when it's actually safe to release the count controller,
    // rather than doing so as soon as the (usually faster) rows fetch alone
    // resolves. Uses its own signal, independent of rows' — a load-more never
    // dispatches a new count, so it must not be able to cancel one that a prior
    // reload is still waiting on.
    const countPromise
      = !skipCount && reload
        ? dispatch('FETCH_AUDIENCE_COUNT', { searchString, filter, signal: countSignal })
        : undefined
    // Attach a rejection observer the moment the promise exists, not after —
    // if rows rejects below, this action exits via the catch/throw below before
    // ever reaching the normal `return { countPromise }`, so nobody downstream
    // would get a chance to observe a late-arriving count rejection otherwise
    // (a genuine unhandled rejection, not just a delayed one). The real
    // handling for a rejection the caller cares about still happens via the
    // returned/attached promise; this is only to guarantee it's never left
    // completely unobserved.
    countPromise?.catch(() => {})
    if (reload) commit('SET_AUDIENCE', [])
    const filteredSelectedColumnKeys = selectKeys?.filter(
      (key: string) => !key.startsWith('cf_'),
    )
    // An explicit `skip` means page-based pagination, which always wants the
    // current page to fully replace the list. `more` means infinite scroll,
    // which appends via requestApi's default CONCAT_CACHE-on-$skip>0 behaviour.
    const paginated = typeof skip === 'number'
    const effectiveSkip = paginated ? skip : (more ? state.audience.length : 0)
    try {
      const result = await requestApi('fetchAudience', {
        params: {
          $top: top,
          $skip: effectiveSkip,
          $orderby: orderByStr,
          $audienceFilter: filterStr,
          $select: filteredSelectedColumnKeys.join(',') || null,
          $count: false,
        },
        signal,
        // For pagination we always want a full replace, so skip requestApi's
        // default CONCAT_CACHE-on-$skip>0 (infinite scroll) append — it would
        // otherwise concat onto the prior page just to have this commit below
        // immediately overwrite it.
        skipCacheAppend: paginated,
      })
      if (paginated) commit('SET_AUDIENCE', result || [])
    }
    catch (error: unknown) {
      // Rows failed — still surface the same error to the caller (unchanged
      // contract for every dispatcher of this action). See AudienceFetchError.
      const typedError = error as AudienceFetchError
      throw Object.assign(typedError, { countPromise })
    }

    return { countPromise }
  },
  // We have to do this call in parallel with FETCH_MORE_AUDIENCE, because it takes
  // a very long time to calculate the "count", so we have to separate it from that action.
  // Uses its own independent countSignal, NOT FETCH_MORE_AUDIENCE's rows signal --
  // a load-more never dispatches a new count, so aborting rows must never be able
  // to cancel a count a prior reload is still waiting on (that would recreate the
  // pagination-cancels-count bug this separation exists to prevent).
  async FETCH_AUDIENCE_COUNT(
    { rootState, state, commit },
    { searchString = '', filter = { conditions: [], logic: [] }, signal = undefined },
  ) {
    const filterStr = JSON.stringify(
      prepareAudienceFilter(filter, searchString),
    )
    return requestApi('fetchAudienceCount', {
      params: {
        $top: 0,
        $skip: 0,
        $audienceFilter: filterStr,
        $select: 'oid',
        $count: true,
      },
      signal,
    })
  },

  async MASS_EDIT_FAN({ rootState }, params) {
    const { suppressNotification, ...restParams } = params
    const body = { ...restParams }
    if (restParams.filter || restParams.searchString) {
      body.filter = prepareAudienceFilter(
        restParams.filter,
        restParams.searchString,
      )
      delete body.searchString
    }
    return requestApi('massEditFans', {
      body,
      ...(suppressNotification ? { suppressSuccess: true } : {}),
    })
  },

  async DELETE_AUDIENCE({ rootState }, { fanOids, filter, searchString }) {
    const filterDecorated = prepareAudienceFilter(filter, searchString)
    const body: any = {}
    if (fanOids) {
      body.fanOids = fanOids
    }
    else {
      body.filter = filterDecorated
    }
    requestApi('massDeleteFans', { body })
  },

  async EXPORT_AUDIENCE_CSV(
    { rootState, commit },
    {
      searchString = '',
      selectKeys,
      filter,
      fans,
      orderBy,
      customFieldData = null,
    }: { [key: string]: any; fans: Audience },
  ) {
    let orderByStr
    if (Array.isArray(orderBy)) {
      orderByStr = orderBy
        .map(item => `${item.key} ${item.order}`)
        .join(', ')
    }
    else {
      orderByStr = `${orderBy.key} ${orderBy.order}`
    }
    let selectedFansFilter: string | null = null
    if (fans && fans.length > 0) {
      selectedFansFilter = JSON.stringify({
        conditions: fans.map(fan => ({
          name: 'oid',
          type: 'numeric',
          data: {
            condition: 'is_equal_to',
            values: [fan.oid],
          },
        })),
        logic: Array(Math.max(fans.length - 1, 0)).fill('or'),
      })
    }
    const filterStr = selectedFansFilter
      || JSON.stringify(
        prepareAudienceFilter(filter, searchString),
      )
    // TODO: (refactor) For some obscure reasons, the export endpoints expects custom fields to be in a different schema than the filter-fan endpoint. Let's try to make this consistent at some point.
    const adjustedSelectKeys = selectKeys.map((key: string) => {
      if (key.startsWith('cf_') && !!customFieldData) {
        const oid = key.substring(3)
        const selectedCustomField = customFieldData.find((customField: any) => {
          return parseInt(oid) === customField.oid
        })
        if (selectedCustomField) {
          let type = ''
          switch (selectedCustomField.fieldType) {
            case 'dropdown-single':
            case 'dropdown-multi':
              type = 'customFieldOptions'
              break
            case 'date':
              type = 'customFieldDatetime'
              break
            case 'number':
              type = 'customFieldInteger'
              break
            case 'text':
            default:
              type = 'customFieldString'
          }
          return `${type}[${oid}] as cf${oid}${convertToSanitizedCamelCase(selectedCustomField.name)}`
        }
      }
      return key
    })
    // Always include subscription status so it's available in the export
    if (!adjustedSelectKeys.includes('emailSubscribed')) {
      adjustedSelectKeys.push('emailSubscribed')
    }
    if (!adjustedSelectKeys.includes('smsSubscribed')) {
      adjustedSelectKeys.push('smsSubscribed')
    }
    const { status, data } = await requestApi('exportAudienceCsv', {
      body: {
        $top: 'all',
        $select: adjustedSelectKeys.join(','),
        $orderby: orderByStr,
        $audienceFilter: filterStr,
      },
    })
    if (status === 200) {
      const fileName = `attendees-${dayjs().format('DD/MM/YYYY')}.csv`
      magicDownload(data, fileName)
    }
  },
  async FETCH_AUDIENCE_OPT_IN_STATISTICS(
    { rootState, commit },
    { messageList, filter, searchString },
  ) {
    const optInStats: AudienceOptInStats = {
      email: { optIns: 0, totalAvailable: 0 },
      sms: { optIns: 0, totalAvailable: 0 },
    }
    const fetchOptInsStats = async (
      audienceFilterStr: string,
      channel: MessageListChannel,
      field: 'optIns' | 'totalAvailable',
    ) => {
      const response = (await requestApi('fetchAudienceOptInsStats', {
        params: {
          $count: 'true',
          $top: '0',
          $audienceFilter: audienceFilterStr,
        },
      })) as { count?: number } | undefined
      const count = response?.count || 0
      optInStats[channel][field] = count
    }
    const correlatedBaseFilter = correlateListSubscriptionChannel(
      convertCurrencyFilterToMinorUnits(filter),
    )
    const generateAudienceFilterStr = (extraFilterConditions: unknown[]) => {
      const filterWithChannel = fanFilterWithExtraConditions(
        correlatedBaseFilter,
        extraFilterConditions,
        Array(extraFilterConditions.length - 1).fill('and'),
      )
      return JSON.stringify(
        mergeSearchStringToFanFilters(
          correlateListSubscriptionChannel(filterWithChannel),
          searchString,
        ),
      )
    }
    const fetchStatsPromises = []
    let audienceFilterStr
    // All opted emails
    audienceFilterStr = generateAudienceFilterStr([
      { name: 'emailAddress', type: 'text', data: { condition: 'is_known' } },
      { name: 'subscribedTo', type: 'select', data: 'email' },
    ])
    fetchStatsPromises.push(
      fetchOptInsStats(audienceFilterStr, 'email', 'optIns'),
    )
    // All opted SMS
    audienceFilterStr = generateAudienceFilterStr([
      { name: 'mobileNumber', type: 'text', data: { condition: 'is_known' } },
      { name: 'subscribedTo', type: 'select', data: 'sms' },
    ])
    fetchStatsPromises.push(
      fetchOptInsStats(audienceFilterStr, 'sms', 'optIns'),
    )
    await Promise.all(fetchStatsPromises)
    commit('SET_AUDIENCE_OPT_INS_STATS', optInStats)
  },

  async IMPORT_ONESCHEMA_CSV(_, payload) {
    await requestApi('importOneSchemaCSV', payload)
  },

  async CREATE_CONTACT(
    { rootState, dispatch, commit },
    {
      payload,
      callback,
    }: { payload: any; callback?: (success: boolean) => void },
  ) {
    try {
      payload.messageLists = payload.messageList?.oid
        ? [
            {
              oid: payload.messageList?.oid,
              subscriptions: {
                email: {
                  enable: payload.messageList.emailEnabled,
                  enableAll: payload.messageList.emailEnabled,
                },
                sms: {
                  enable: payload.messageList.smsEnabled,
                  enableAll: payload.messageList.smsEnabled,
                },
              },
            },
          ]
        : undefined

      payload.fans = payload.fans.map((fan: any) =>
        Object.entries(fan).reduce((acc: any, [key, value]) => {
          if (
            value !== undefined
            && value !== null
            && value !== ''
            && key !== 'messageList'
          ) {
            acc[key] = value
          }
          return acc
        }, {}),
      )
      delete payload.messageList
      await requestApi('createContact', { body: payload })

      if (callback) {
        callback(true)
      }
    }
    catch (error) {
      console.error('Error creating contact:', error)
      if (callback) {
        callback(false)
      }
    }
  },

  async UPDATE_CONTACT(
    _: any,
    {
      payload,
      callback,
    }: { payload: any; callback?: (success: boolean) => void },
  ) {
    try {
      payload.messageLists = payload.messageList?.oid
        ? [
            {
              oid: payload.messageList?.oid,
              subscriptions: {
                email: {
                  enable: payload.messageList.emailEnabled,
                  enableAll: payload.messageList.emailEnabled,
                },
                sms: {
                  enable: payload.messageList.smsEnabled,
                  enableAll: payload.messageList.smsEnabled,
                },
              },
            },
          ]
        : undefined

      // For updates, preserve empty strings for text fields but exclude empty strings for special fields
      payload.fans = payload.fans.map(fan =>
        Object.entries(fan).reduce((acc, [key, value]) => {
          // Only exclude undefined, null, and messageList (handled separately)
          if (value !== undefined && value !== null && key !== 'messageList') {
            // For certain fields, don't send empty strings as they may cause backend issues
            if (value === '' && (key === 'dob' || key === 'mobileNumber')) {
              // Skip empty strings for date and mobile fields
              return acc
            }
            // Keep all other values including empty strings for text fields
            acc[key] = value
          }
          return acc
        }, {}),
      )

      // Remove the original messageList field since we've converted it to messageLists
      delete payload.messageList
      await requestApi('createContact', { body: payload })

      if (callback) {
        callback(true)
      }
    }
    catch (error) {
      console.error('Error updating contact:', error)
      if (callback) {
        callback(false)
      }
    }
  },
}
